import { mobileAccess, processViewPartHtml, virtualDir } from "#skyward_global";
import { ModalLayer, ModalLayerOptions, newModalLayer } from "#skyward_modalLayer";
import { getClientHeight, loadContent, notifyDOMLoad, registerLayoutResizeCallback } from "#skyward_window";
import { encodeHtml } from "./skyward.base.extensions";
import { setFocusToTextBoxOnClick } from "./skyward.ui.eel";
import { registerEvents } from "./skyward.ui.events";
import { JQueryInput } from "./types";

interface KeyValuePair
{
    Key: number;
    Value: string;
}

interface SelectorTemplateData extends Record<string, unknown>
{
    templatedResults: KeyValuePair[];
    seekText?: string;
}

const skyward = window.skyward;

const events =
{
    init
};

registerEvents("selectorEEL", events);

function init(selector: JQueryInput)
{
    // Find the necessary parts of the control
    const selectorEEL = $(selector);
    const selectorInput = selectorEEL.find(".selectorEELInput"); //search textbox
    const selectorSearch = selectorEEL.find(".selectorEELSearch"); //search button

    // hidden values
    const selectorValue = selectorEEL.find(".selectorEELValue"); //hidden value
    const selectorData = selectorValue.data();
    const searchModule = selectorData.searchModule;
    const searchObject = selectorData.searchObject;
    const redirectModule = selectorData.redirectModule;
    const redirectObject = selectorData.redirectObject;
    const redirectScreen = selectorData.redirectScreen;
    let oldSelectorContent = "";
    let popupContent: JQuery<HTMLElement>;
    let layer: ModalLayer;

    setFocusToTextBoxOnClick(selectorEEL);

    let lastRequest: JQuery.jqXHR<any>;

    selectorInput.on("keydown" as any, keydownHandler);
    selectorInput.debounce("keyup", keyupHandler);
    selectorSearch.on("click", function () 
    {
        if (layer != null && layer.onscreen) 
        {
            if (!mobileAccess)
            {
                layer.close();
            }
            return;
        }
        showSelectorPopup();
    });

    registerLayoutResizeCallback(function ()
    {
        if (!layer || !layer.onscreen || $.isEmptyObject(popupContent))
        {
            return;
        }

        if (mobileAccess)
        {
            calculateMobilePopupHeight();
        }
        else
        {
            const popupContentElement = $(popupContent);
            const currentSelectedRow = popupContentElement.find(".templatedResult.selected").first();
            const rowForSizing = currentSelectedRow.length > 0 ? currentSelectedRow : popupContentElement.find(".templatedResult").first();
            if (rowForSizing.length === 0)
            {
                return;
            }

            calculatePopupHeight(rowForSizing);
        }
    });

    function keydownHandler(event: KeyboardEvent)
    {
        const eventKeys = skyward.keys.getKeyNamesFromEvent(event);
        const goUp = eventKeys.ARROW_LEFT || eventKeys.ARROW_UP;
        const goDown = eventKeys.ARROW_RIGHT || eventKeys.ARROW_DOWN;
        const goInto = eventKeys.ENTER;
        const exit = eventKeys.TAB;

        if (!layer || !layer.onscreen)
        {
            if (goUp || goDown)
            {
                showSelectorPopup();
            }
        }
        else if (goUp || goDown || goInto)
        {
            const selectedRow = layer.contents.find(".templatedResult.selected");
            if (selectedRow.length === 0)
            {
                return;
            }

            if (goUp || goDown) 
            {
                let nextRow;
                if (goUp)
                {
                    nextRow = selectedRow.prev();
                }
                else
                {
                    nextRow = selectedRow.next();
                }

                updateSelectedRow(selectedRow, nextRow);
                event.preventDefault();
            }
            else
            {
                selectedRow.trigger("click");
            }
        }
        else if (exit)
        {
            layer.close();
        }
    }

    //Get the templated information
    function getSelectorEELData()
    {
        if (lastRequest !== undefined)
        {
            lastRequest.abort();
        }

        lastRequest = $.ajax(
            {
                url: virtualDir + searchModule + "/" + searchObject + "/SelectorTemplate",
                type: "POST",
                dataType: "JSON",
                data:
                {
                    seekText: encodeHtml(selectorInput.val() as string)
                },
                success: async function (data: SelectorTemplateData)
                {
                    if ($.isEmptyObject(popupContent))
                    {
                        return;
                    }

                    const popupContentElement = $(popupContent);

                    //Only do this if we have results
                    if (data.templatedResults.length > 0)
                    {
                        const contents = [];
                        const idPrefix = selectorValue.attr("id") + "-item-";

                        //Add the templated results to the div
                        for (const templatedResult of data.templatedResults)
                        {
                            const id = idPrefix + templatedResult.Key;
                            const labelId = id + "-label";
                            const descriptionId = id + "-description";;

                            const processedValue = await processViewPartHtml(templatedResult.Value);
                            let html = processedValue;
                            if (typeof processedValue !== 'string')
                            {
                                html = processedValue.toOuterHtmlString();
                            }

                            const item = $("<div>", {
                                id: id,
                                class: "templatedResult selectorElementsCenter",
                                "data-key": templatedResult.Key,
                                html: html,
                                role: "menuitem"
                            });

                            const labelArea = item.find(".js-labelArea");
                            if (labelArea.length)
                            {
                                labelArea.attr("id", labelId);
                                item.attr("aria-labelledby", labelId);
                            }

                            const descriptionArea = item.find(".js-descriptionArea");
                            if (descriptionArea.length)
                            {
                                descriptionArea.attr("id", descriptionId);
                                item.attr("aria-describedby", descriptionId);
                            }

                            contents.push(item);
                        }
                        popupContentElement.empty().append(contents);

                        const selectedRow = popupContentElement.find(".templatedResult:first").addClass("selected");
                        selectorInput.attr("aria-activedescendant", selectedRow.addIds().attr("id")!);

                        if (mobileAccess)
                        {
                            calculateMobilePopupHeight();
                        }
                        else
                        {
                            calculatePopupHeight(selectedRow);
                        }

                        notifyDOMLoad("#" + popupContentElement.attr("id"));
                    }
                    else
                    {
                        popupContentElement.empty().append($("<div>",
                            {
                                class: "noRecordsInBrowse browseRow templatedResult ignoreClickEvent selectorElementsCenter",
                                html: "<p>No records to display</p>"
                            })
                        );
                    }
                }
            });
    }

    function calculatePopupHeight(selectedRow: JQuery<HTMLElement>)
    {
        if ($.isEmptyObject(popupContent))
        {
            return;
        }

        const maxHeight = parseInt(popupContent.css("maxHeight"));
        let popupHeight = 0;
        const numberOfRecords = $(popupContent).children().length;
        const selectedRowHeight = selectedRow.outerHeight(true)!;
        if (numberOfRecords * selectedRowHeight > maxHeight)
        {
            popupHeight = Math.floor(maxHeight / selectedRowHeight) * selectedRowHeight;
        }
        else
        {
            popupHeight = numberOfRecords * selectedRowHeight;
        }

        popupContent.height(popupHeight);
    }

    function calculateMobilePopupHeight()
    {
        if ($.isEmptyObject(popupContent))
        {
            return;
        }

        const popupTop = popupContent.offset()!.top;
        // could use calc(100lvh - var(soft-keyboard-height)) but it's only supported on Safari 15+
        // additionally we resize the webview itself, so lvh doesn't work the way it's supposed to
        const fullHeight = window.innerHeight;
        popupContent.css(
            {
                maxHeight: `calc(100vh - ${popupTop}px)`
            });
    }

    function updateSelectedRow(selectedRow: JQuery<HTMLElement>, nextRow: JQuery<HTMLElement>)
    {
        if ($.isEmptyObject(nextRow) || nextRow.length === 0 || $.isEmptyObject(popupContent))
        {
            return;
        }

        selectedRow.removeClass("selected");
        nextRow.addClass("selected");

        nextRow.scrollintoview(
            {
                duration: 0,
                direction: "vertical",
                viewPadding: { y: 0 }
            });
        selectorInput.attr("aria-activedescendant", nextRow.addIds().attr("id")!);
    }

    function keyupHandler(_event: KeyboardEvent)
    {
        // Do not call if readonly or the contents haven't changed.
        if (!selectorInput.attr("readonly") && selectorInput.val() !== oldSelectorContent)
        {
            oldSelectorContent = selectorInput.val() as string;
            if (!layer || !layer.onscreen)
            {
                showSelectorPopup();
            }
            else
            {
                getSelectorEELData();
            }
        }
    }

    function clickHandler(this: JQueryInput)
    {
        loadContent("full", redirectModule, redirectObject, redirectScreen, $(this).data("key"));
    }

    function showSelectorPopup()
    {
        if (selectorInput.attr("readonly"))
        {
            return;
        }

        let body: JQuery<HTMLElement>;
        const offset = selectorInput.offset()!;
        const height = selectorInput.outerHeight()!;
        const popupDivName = selectorInput.attr("id") + "_popup";
        const maxHeight = getClientHeight() * 0.85 - offset.top - height;

        popupContent = $("<div>",
            {
                id: popupDivName,
                class: "verticalScroll popup anchored noAnchoredTail",
                css:
                {
                    maxHeight: mobileAccess ? "none" : maxHeight + "px",
                    left: offset.left,
                    top: offset.top + height
                }
            });
        popupContent.on("click", ".templatedResult:not(.ignoreClickEvent)", clickHandler);

        const modalLayerOptions: ModalLayerOptions =
        {
            expose: [selectorInput.closest(".js-showEELContainer")],
            role: "menu"
        };

        if (mobileAccess)
        {
            body = $(document.body);
            // the mobileEelOpen class will disable momentum scrolling, which causes issues with exposing elements through the glass
            body.addClass('mobileEelOpen');

            modalLayerOptions.afterClose = function ()
            {
                // reselect document.body
                body.removeClass('mobileEelOpen');
            };
        }

        layer = newModalLayer(popupContent, function ()
        {
            popupContent = null as any;
        }, modalLayerOptions);

        getSelectorEELData();
        selectorInput.trigger("focus");
    }
}
